import React, { useEffect, useMemo } from 'react'
import { Formik } from 'formik'
import { Row, Col, Form, FormGroup } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { get } from 'lodash'
import Select from 'ui/forms/select'
import TextInput from 'ui/forms/text_input'
import DatePicker from 'ui/forms/datetime_picker'
import QtyInput from 'ui/forms/qty_input'
import * as Yup from 'yup'

const pickupPoints = [
  { label: 'Aeropuerto Barcelona-El Prat T1', value: 'terminal_1' },
  { label: 'Aeropuerto Barcelona-El Prat T2', value: 'terminal_2' },
  { label: 'Estación de Sants', value: 'sants_station' },
  { label: 'Puerto de Barcelona', value: 'port_of_barcelona' },
  { label: 'Barcelona ciudad', value: 'barcelona_city' },
]

const ALLOW_REPEAT_VALUE = 'barcelona_city'
const AIRPORT_VALUES = ['terminal_1', 'terminal_2']

const RouteForm = (props) => {
  const { i18n } = useTranslation()
  const { config, machine } = props
  const release = get(config, 'product.external_api_config.release')

  const incrementQty = (currentQty, maxQty) => (currentQty >= maxQty ? maxQty : currentQty + 1)
  const decrementQty = (currentQty, minQty) => (currentQty <= minQty ? minQty : currentQty - 1)

  // ✅ Validaciones Yup basadas en visibilidad (selecciones)
  const validationSchema = useMemo(() => {
    const requiredMsg = i18n.t('validations:required')

    return Yup.object().shape({
      pickup_type: Yup.string().required(requiredMsg),
      dropoff_type: Yup.string().required(requiredMsg),

      pickup_date: Yup.mixed()
        .required(requiredMsg)
        .test('valid-date', requiredMsg, (v) => !!v), // por si DatePicker retorna Date/string

      // Cantidades (por si alguien edita a mano o llega desde API)
      adults: Yup.number().typeError(requiredMsg).integer().min(1).max(20).required(requiredMsg),
      children: Yup.number().typeError(requiredMsg).integer().min(0).max(20).required(requiredMsg),
      infants: Yup.number().typeError(requiredMsg).integer().min(0).max(20).required(requiredMsg),

      // Visible SOLO si pickup es aeropuerto
      pickup_flight_number: Yup.string().when('pickup_type', {
        is: (v) => AIRPORT_VALUES.includes(v),
        then: (s) => s.trim().required(requiredMsg),
        otherwise: (s) => s.trim().nullable().notRequired(),
      }),

      // Visible SOLO si dropoff es aeropuerto
      dropoff_flight_number: Yup.string().when('dropoff_type', {
        is: (v) => AIRPORT_VALUES.includes(v),
        then: (s) => s.trim().required(requiredMsg),
        otherwise: (s) => s.trim().nullable().notRequired(),
      }),

      // Visible SOLO si pickup es Barcelona ciudad
      pickup_address: Yup.string().when('pickup_type', {
        is: (v) => v === 'barcelona_city',
        then: (s) => s.trim().required(requiredMsg),
        otherwise: (s) => s.trim().nullable().notRequired(),
      }),

      // Visible SOLO si dropoff es Barcelona ciudad
      dropoff_address: Yup.string().when('dropoff_type', {
        is: (v) => v === 'barcelona_city',
        then: (s) => s.trim().required(requiredMsg),
        otherwise: (s) => s.trim().nullable().notRequired(),
      }),
    })
  }, [i18n])

  return (
    <Formik
      initialValues={{
        pickup_type: '',
        dropoff_type: '',
        adults: 1,
        children: 0,
        infants: 0,
        pickup_flight_number: '',
        dropoff_flight_number: '',
        pickup_address: '',
        dropoff_address: '',
        pickup_date: '',
      }}
      validationSchema={validationSchema}
      onSubmit={() => {}}
      validateOnBlur
      validateOnChange
    >
      {(form) => {
        const { values, errors, touched, setFieldValue } = form

        // Excluir el valor del otro select, EXCEPTO si es "barcelona_city"
        const pickupOptions =
          values.dropoff_type && values.dropoff_type !== ALLOW_REPEAT_VALUE
            ? pickupPoints.filter((p) => p.value !== values.dropoff_type)
            : pickupPoints

        const dropoffOptions =
          values.pickup_type && values.pickup_type !== ALLOW_REPEAT_VALUE
            ? pickupPoints.filter((p) => p.value !== values.pickup_type)
            : pickupPoints

        // Mantener consistencia: si quedan iguales, solo limpiar si NO es "barcelona_city"
        useEffect(() => {
          props.setForm(form)

          if (
            values.pickup_type &&
            values.pickup_type === values.dropoff_type &&
            values.pickup_type !== ALLOW_REPEAT_VALUE
          ) {
            setFieldValue('dropoff_type', '')
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [values.pickup_type])

        useEffect(() => {
          if (
            values.dropoff_type &&
            values.dropoff_type === values.pickup_type &&
            values.dropoff_type !== ALLOW_REPEAT_VALUE
          ) {
            setFieldValue('pickup_type', '')
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [values.dropoff_type])

        // Limpieza opcional de campos ocultos (evita enviar basura y evita errores “fantasma”)
        useEffect(() => {
          if (!AIRPORT_VALUES.includes(values.pickup_type) && values.pickup_flight_number) {
            setFieldValue('pickup_flight_number', '')
          }
          if (values.pickup_type !== 'barcelona_city' && values.pickup_address) {
            setFieldValue('pickup_address', '')
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [values.pickup_type])

        useEffect(() => {
          if (!AIRPORT_VALUES.includes(values.dropoff_type) && values.dropoff_flight_number) {
            setFieldValue('dropoff_flight_number', '')
          }
          if (values.dropoff_type !== 'barcelona_city' && values.dropoff_address) {
            setFieldValue('dropoff_address', '')
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [values.dropoff_type])

        return (
          <Form>
            <Row className="justify-content-md-center">
              <Col xs="12" md="6">
                <FormGroup>
                  <Select
                    name="pickup_type"
                    label={i18n.t('route_form.pickup_type')}
                    defaultValue={null}
                    values={pickupOptions}
                    placeholder={i18n.t('route_form.pickup_type_placeholder')}
                    required
                  />

                  {(values.pickup_type === 'terminal_1' || values.pickup_type === 'terminal_2') && (
                    <TextInput
                      label={i18n.t('route_form.flight_number')}
                      name="pickup_flight_number"
                      required
                    />
                  )}

                  {values.pickup_type === 'barcelona_city' && (
                    <TextInput
                      label={i18n.t('route_form.pickup_address')}
                      name="pickup_address"
                      required
                    />
                  )}

                  <DatePicker
                    machine={machine}
                    name="pickup_date"
                    release={release}
                    label={i18n.t('route_form.pickup_date')}
                    timezone={config.product.local_timezone}
                  />
                </FormGroup>
              </Col>

              <Col xs="12" md="6">
                <FormGroup>
                  <Select
                    name="dropoff_type"
                    label={i18n.t('route_form.dropoff_type')}
                    defaultValue={null}
                    values={dropoffOptions}
                    placeholder={i18n.t('route_form.dropoff_type_placeholder')}
                    required
                  />

                  {(values.dropoff_type === 'terminal_1' || values.dropoff_type === 'terminal_2') && (
                    <TextInput
                      label={i18n.t('route_form.flight_number')}
                      name="dropoff_flight_number"
                      required
                    />
                  )}

                  {values.dropoff_type === 'barcelona_city' && (
                    <TextInput
                      label={i18n.t('route_form.dropoff_address')}
                      name="dropoff_address"
                      required
                    />
                  )}

                  <Row>
                    <Col xs="12" md="4">
                      <QtyInput
                        label={i18n.t('route_form.adults')}
                        name="adults"
                        value={values.adults}
                        onIncrement={() => setFieldValue('adults', incrementQty(values.adults, 20))}
                        onDecrement={() => setFieldValue('adults', decrementQty(values.adults, 1))}
                      />
                    </Col>

                    <Col xs="12" md="4">
                      <QtyInput
                        label={i18n.t('route_form.children')}
                        name="children"
                        value={values.children}
                        onIncrement={() =>
                          setFieldValue('children', incrementQty(values.children, 20))
                        }
                        onDecrement={() =>
                          setFieldValue('children', decrementQty(values.children, 0))
                        }
                      />
                    </Col>

                    <Col xs="12" md="4">
                      <QtyInput
                        label={i18n.t('route_form.infants')}
                        name="infants"
                        value={values.infants}
                        onIncrement={() =>
                          setFieldValue('infants', incrementQty(values.infants, 20))
                        }
                        onDecrement={() =>
                          setFieldValue('infants', decrementQty(values.infants, 0))
                        }
                      />
                    </Col>
                  </Row>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        )
      }}
    </Formik>
  )
}

export default RouteForm
